import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { api } from '../../api/api';

const normalizeEventId = value => {
  if (value === null || value === undefined || value === '' || value === 'none') {
    return null;
  }
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const getTemplateEventId = template => {
  return (
    template?.event_id ||
    template?.eventId ||
    (template?.Event ? template.Event.id : null) ||
    null
  );
};

const TemplateMoveDialog = ({
  open,
  onClose,
  template,
  mode = 'move',
  user,
  showNotification,
  onMoved,
  fallbackEvents = [],
  shouldAutoFetch = true,
}) => {
  const [events, setEvents] = useState(fallbackEvents);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentEventId = useMemo(() => getTemplateEventId(template), [template]);
  const userIsAdmin = user?.role?.name === 'Admin';

  const loadEvents = useCallback(async () => {
    if (!user) return;
    setIsLoadingEvents(true);
    try {
      const res = await api.getEvents();
      setEvents(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error('Failed to load events', error);
      setEvents([]);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [user]);

  useEffect(() => {
    if (!open) return;
    setErrorMessage('');
    setIsSubmitting(false);
    const initialValue = currentEventId !== null ? String(currentEventId) : 'none';
    setSelectedEventId(initialValue);

    if (Array.isArray(fallbackEvents) && fallbackEvents.length > 0) {
      setEvents(prev => {
        const prevSignature = prev.map(evt => evt.id).join(',');
        const nextSignature = fallbackEvents.map(evt => evt.id).join(',');
        return prevSignature === nextSignature ? prev : fallbackEvents;
      });
    }

    if (Array.isArray(fallbackEvents) && fallbackEvents.length > 0) {
      return;
    }
    if (shouldAutoFetch) {
      loadEvents();
    }
  }, [open, currentEventId, fallbackEvents, loadEvents, shouldAutoFetch]);

  const availableEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return events
      .filter(evt => {
        if (!evt) return false;
        if (!userIsAdmin && evt.status === 'archived') return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [events, userIsAdmin]);

  const currentEventLabel = useMemo(() => {
    if (currentEventId === null) return 'General library';
    const current = availableEvents.find(
      evt => normalizeEventId(evt.id) === normalizeEventId(currentEventId)
    );
    return current ? current.name : `Event #${currentEventId}`;
  }, [availableEvents, currentEventId]);

  const isCopyMode = mode === 'copy';

  if (!open) return null;

  const closeDialog = () => {
    if (isSubmitting) return;
    onClose?.();
  };

  const handleBackdropClick = event => {
    if (event.target === event.currentTarget) {
      closeDialog();
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setErrorMessage('');

    if (!template || !template.id) {
      setErrorMessage('Template data missing.');
      return;
    }

    const normalizedTarget = normalizeEventId(selectedEventId);
    const normalizedCurrent = normalizeEventId(currentEventId);

    if (!isCopyMode && normalizedTarget === normalizedCurrent) {
      setErrorMessage('Select a different event to move this template.');
      return;
    }

    setIsSubmitting(true);
    try {
      const ids = [template.id];
      if (isCopyMode) {
        await api.bulkCopyTemplates(ids, normalizedTarget);
      } else {
        await api.bulkMoveTemplates(ids, normalizedTarget);
      }
      const targetEvent = availableEvents.find(
        evt => normalizeEventId(evt.id) === normalizedTarget
      );
      const targetLabel = normalizedTarget
        ? targetEvent?.name || 'selected event'
        : 'General library';
      showNotification(
        isCopyMode
          ? `Template copied to ${targetLabel}.`
          : `Template moved to ${targetLabel}.`,
        'success'
      );
      onMoved?.(normalizedTarget);
      onClose?.();
    } catch (error) {
      console.error('Failed to process template', error);
      setErrorMessage(
        error?.message ||
          (isCopyMode ? 'Failed to copy template.' : 'Failed to move template.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDisabled =
    isSubmitting ||
    availableEvents.length === 0 ||
    (!isCopyMode &&
      normalizeEventId(selectedEventId) === normalizeEventId(currentEventId));

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4 py-6 overflow-y-auto"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 sm:p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isCopyMode ? 'Copy template' : 'Move template'}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {isCopyMode
                ? 'Copy this template to a different event folder. You will be able to edit the copied version.'
                : 'Move this template to a different event folder. Users with access to that event will see it immediately.'}
            </p>
          </div>
          <button
            onClick={closeDialog}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="move-event">
              Destination event
            </label>
            {availableEvents.length > 0 ? (
              <select
                id="move-event"
                className="form-select w-full"
                value={selectedEventId}
                onChange={event => setSelectedEventId(event.target.value)}
                disabled={isLoadingEvents}
              >
              <option value="none">General library (no event)</option>
                {availableEvents.map(evt => (
                  <option key={evt.id} value={String(evt.id)}>
                    {evt.name}
                    {evt.status === 'archived' ? ' (Archived)' : ''}
                  </option>
                ))}
              </select>
            ) : isLoadingEvents ? (
              <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-gray-500">
                Loading events…
              </div>
            ) : (
              <p className="text-xs text-amber-600">
                No events available to move this template. Create an event first or contact an administrator.
              </p>
            )}
            <p className="text-xs text-gray-500">
              Current location: <strong>{currentEventLabel}</strong>
            </p>
          </div>

          {errorMessage && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMessage}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={closeDialog}
              className="nav-button-secondary w-full sm:w-auto"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="nav-button-primary w-full sm:w-auto"
              disabled={submitDisabled}
            >
              {isSubmitting
                ? isCopyMode
                  ? 'Copying…'
                  : 'Moving…'
                : isCopyMode
                ? 'Copy template'
                : 'Move template'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default TemplateMoveDialog;
